import React, { useState, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { API_BASE_URL } from '../../../config/api';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend,
    LineChart,
    Line,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
} from 'recharts';
import {
    MdPeople,
    MdTrendingUp,
    MdStar,
    MdCheckCircle,
    MdPictureAsPdf,
    MdBarChart,
} from 'react-icons/md';
import Select from 'react-select';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Analytics.css';

/* ── Chart colour palettes ── */
const PIE_COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE', '#6366F1'];
const BAR_GRADIENT_IDS = ['barGrad0', 'barGrad1', 'barGrad2', 'barGrad3'];
const BAR_GRADIENTS = [
    ['#4F46E5', '#818CF8'],
    ['#059669', '#34D399'],
    ['#D97706', '#FBBF24'],
    ['#E11D48', '#FB7185'],
];

/* ── Quarter filter options ── */
const quarterOptions = [
    { value: 'Q1', label: 'Quarter 1' },
    { value: 'Q2', label: 'Quarter 2' },
    { value: 'Q3', label: 'Quarter 3' },
    { value: 'Q4', label: 'Quarter 4' },
];

/* ── Custom tooltip component ── */
const CustomTooltip = ({ active, payload, label, suffix = '' }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="analytics-tooltip">
            <div className="label">{label}</div>
            {payload.map((entry, i) => (
                <div key={i} className="value" style={{ color: entry.color || '#fff' }}>
                    {entry.name}: {formatNumber(Number(entry.value))}{suffix}
                </div>
            ))}
        </div>
    );
};

/* ── Number formatting ── */
const formatNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isInteger(value) ? value : value.toFixed(2);
    }
    return value;
};

/* ================================================================== */
/*  Analytics Component                                                */
/* ================================================================== */
const Analytics = () => {
    const [courseCenters, setCourseCenters] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedCourseCenter, setSelectedCourseCenter] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedQuarter, setSelectedQuarter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const baseUrl = API_BASE_URL;

    /* ── Fetch filter options ── */
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const token = Cookies.get('token');
                const [ccRes, depRes] = await Promise.all([
                    axios.get(`${baseUrl}/api/course-centers/`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${baseUrl}/api/departments/`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                setCourseCenters(ccRes.data);
                setDepartments(depRes.data);
            } catch {
                setCourseCenters([]);
                setDepartments([]);
            }
        };
        fetchOptions();
    }, [baseUrl]);

    /* ── Fetch analytics data ── */
    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            try {
                const token = Cookies.get('token');
                const params = {
                    filtering_method: 'filter',
                    course_center: selectedCourseCenter?.id,
                    department: selectedDepartment?.id,
                    year: selectedYear,
                    quarter: selectedQuarter?.value,
                };
                Object.keys(params).forEach((key) => params[key] == null && delete params[key]);
                const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params,
                });
                setAnalytics(response.data);
            } catch {
                setAnalytics(null);
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
    }, [baseUrl, selectedCourseCenter, selectedDepartment, selectedYear, selectedQuarter]);

    /* ── Derived data ── */
    const stats = analytics?.overall_statistics || {};

    const quarterlyTrendsData = useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, s]) => ({
            quarter,
            ...s,
        }));
    }, [analytics]);

    const statusDistribution = analytics?.status_distribution || [];
    const totalGoals = useMemo(
        () => statusDistribution.reduce((sum, item) => sum + (item.count || 0), 0),
        [statusDistribution]
    );

    const courseCenterStatsData = useMemo(() => {
        if (!analytics?.course_center_statistics) return [];
        return Object.entries(analytics.course_center_statistics).map(([name, s]) => ({
            name,
            ...s,
        }));
    }, [analytics]);

    const departmentStatsData = useMemo(() => {
        if (!analytics?.department_statistics) return [];
        return Object.entries(analytics.department_statistics).map(([name, s]) => ({
            name,
            ...s,
        }));
    }, [analytics]);

    /* Radar-chart-ready department data (capped to 100 for nice radar shape) */
    const radarData = useMemo(() => {
        return departmentStatsData.map((d) => ({
            department: d.name?.length > 14 ? d.name.slice(0, 12) + '…' : d.name,
            performance: Math.min(parseFloat(d.average_progress || 0), 100),
        }));
    }, [departmentStatsData]);

    /* ── Select options ── */
    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        return Array.from({ length: Math.max(1, current - 2025 + 2) }, (_, i) => 2025 + i);
    }, []);
    const yearSelectOptions = useMemo(
        () => yearOptions.map((y) => ({ value: y, label: String(y) })),
        [yearOptions]
    );
    const costCenterOptions = useMemo(
        () => [
            { value: '', label: 'All Cost Centers' },
            ...courseCenters.map((cc) => ({ value: cc.id, label: cc.name, raw: cc })),
        ],
        [courseCenters]
    );
    const departmentOptions = useMemo(
        () => [
            { value: '', label: 'All Departments' },
            ...departments.map((dep) => ({ value: dep.id, label: dep.name, raw: dep })),
        ],
        [departments]
    );

    /* ================================================================ */
    /*  PDF Report Generator                                            */
    /* ================================================================ */
    const generatePDFReport = () => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 20;
        let yPosition = 20;

        // Title
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Performance Analytics Report', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;

        // Date
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const currentDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
        pdf.text(`Generated on: ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 20;

        // Filters
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Applied Filters:', margin, yPosition);
        yPosition += 10;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const filters = [];
        if (selectedCourseCenter) filters.push(`Cost Center: ${selectedCourseCenter.name}`);
        if (selectedDepartment) filters.push(`Department: ${selectedDepartment.name}`);
        if (selectedYear) filters.push(`Year: ${selectedYear}`);
        if (selectedQuarter) filters.push(`Quarter: ${selectedQuarter.label}`);
        if (filters.length === 0) filters.push('All filters applied (no specific selection)');
        filters.forEach((filter) => {
            pdf.text(filter, margin + 5, yPosition);
            yPosition += 6;
        });
        yPosition += 15;

        // Overall Statistics
        if (analytics?.overall_statistics) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Overall Statistics', margin, yPosition);
            yPosition += 10;
            const statsData = [
                ['Metric', 'Value'],
                ['Total Employees', formatNumber(stats.total_employees || 0)],
                ['Average Performance', `${formatNumber(stats.average_progress || 0)}%`],
                ['Completion Rate', `${formatNumber(stats.completion_rate || 0)}%`],
                ['Completed Goals', `${formatNumber(stats.completed_goals || 0)} / ${formatNumber(stats.total_goals || 0)}`],
            ];
            autoTable(pdf, {
                startY: yPosition,
                head: [statsData[0]],
                body: statsData.slice(1),
                theme: 'grid',
                styles: { fontSize: 10 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin },
            });
            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Quarterly Trends
        if (quarterlyTrendsData.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Quarterly Trends', margin, yPosition);
            yPosition += 10;
            const qHeaders = ['Quarter', 'Completion Rate (%)', 'Total Goals', 'Completed Goals'];
            const qBody = quarterlyTrendsData.map((item) => [
                item.quarter,
                `${formatNumber(Number(item.completion_rate || 0))}%`,
                formatNumber(item.total_goals || 0),
                formatNumber(item.completed_goals || 0),
            ]);
            autoTable(pdf, {
                startY: yPosition,
                head: [qHeaders],
                body: qBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin },
            });
            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Cost Center Statistics
        if (courseCenterStatsData.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Cost Center Performance', margin, yPosition);
            yPosition += 10;
            const ccHeaders = ['Cost Center', 'Avg Performance (%)', 'Total Goals', 'Completed Goals'];
            const ccBody = courseCenterStatsData.map((item) => [
                item.name,
                `${formatNumber(Number(item.average_progress || 0))}%`,
                formatNumber(item.total_goals || 0),
                formatNumber(item.completed_goals || 0),
            ]);
            autoTable(pdf, {
                startY: yPosition,
                head: [ccHeaders],
                body: ccBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin },
            });
            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Department Statistics
        if (departmentStatsData.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Department Performance', margin, yPosition);
            yPosition += 10;
            const dHeaders = ['Department', 'Avg Performance (%)', 'Total Goals', 'Completed Goals'];
            const dBody = departmentStatsData.map((item) => [
                item.name,
                `${formatNumber(Number(item.average_progress || 0))}%`,
                formatNumber(item.total_goals || 0),
                formatNumber(item.completed_goals || 0),
            ]);
            autoTable(pdf, {
                startY: yPosition,
                head: [dHeaders],
                body: dBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin },
            });
            yPosition = pdf.lastAutoTable.finalY + 15;
        }

        // Status Distribution
        if (statusDistribution.length > 0) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Goal Status Distribution', margin, yPosition);
            yPosition += 10;
            const sHeaders = ['Status', 'Count', 'Percentage (%)'];
            const sBody = statusDistribution.map((item) => [
                item.status || 'Unknown',
                formatNumber(item.count || 0),
                `${totalGoals > 0 ? formatNumber(((item.count || 0) / totalGoals) * 100) : 0}%`,
            ]);
            autoTable(pdf, {
                startY: yPosition,
                head: [sHeaders],
                body: sBody,
                theme: 'grid',
                styles: { fontSize: 9 },
                headStyles: { fillColor: [79, 70, 229] },
                margin: { left: margin, right: margin },
            });
        }

        const dateStr = new Date().toISOString().slice(0, 10);
        pdf.save(`Performance_Analytics_Report_${dateStr}.pdf`);
    };

    /* ================================================================ */
    /*  Render                                                          */
    /* ================================================================ */
    return (
        <>
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="analytics-page-wrapper">

                    {/* ─── Header ─── */}
                    <div className="analytics-header">
                        <div className="analytics-header-top">
                            <div>
                                <h1>Reports &amp; Analytics</h1>
                                <p>Filter and analyze organization-wide performance data</p>
                            </div>
                            <button
                                className="analytics-generate-btn"
                                onClick={generatePDFReport}
                                disabled={loading}
                            >
                                <MdPictureAsPdf size={18} />
                                Generate Report
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="analytics-filter-row">
                            <div className="analytics-filter">
                                <label htmlFor="cost-center-select">Cost Center</label>
                                <Select
                                    inputId="cost-center-select"
                                    classNamePrefix="analytics-select"
                                    options={costCenterOptions}
                                    value={
                                        selectedCourseCenter
                                            ? { value: selectedCourseCenter.id, label: selectedCourseCenter.name }
                                            : costCenterOptions[0]
                                    }
                                    onChange={(option) => {
                                        if (!option || !option.value) {
                                            setSelectedCourseCenter(null);
                                        } else {
                                            setSelectedCourseCenter(courseCenters.find((c) => c.id === option.value) || null);
                                        }
                                    }}
                                    isClearable
                                    isSearchable
                                    placeholder="All Cost Centers"
                                />
                            </div>
                            <div className="analytics-filter">
                                <label htmlFor="department-select">Department</label>
                                <Select
                                    inputId="department-select"
                                    classNamePrefix="analytics-select"
                                    options={departmentOptions}
                                    value={
                                        selectedDepartment
                                            ? { value: selectedDepartment.id, label: selectedDepartment.name }
                                            : departmentOptions[0]
                                    }
                                    onChange={(option) => {
                                        if (!option || !option.value) {
                                            setSelectedDepartment(null);
                                        } else {
                                            setSelectedDepartment(departments.find((d) => d.id === option.value) || null);
                                        }
                                    }}
                                    isClearable
                                    isSearchable
                                    placeholder="All Departments"
                                />
                            </div>
                            <div className="analytics-filter">
                                <label htmlFor="year-select">Year</label>
                                <Select
                                    inputId="year-select"
                                    classNamePrefix="analytics-select"
                                    options={yearSelectOptions}
                                    value={{ value: selectedYear, label: String(selectedYear) }}
                                    onChange={(option) => option && setSelectedYear(option.value)}
                                    isSearchable
                                    menuPlacement="auto"
                                />
                            </div>
                            <div className="analytics-filter">
                                <label htmlFor="quarter-select">Quarter</label>
                                <Select
                                    inputId="quarter-select"
                                    classNamePrefix="analytics-select"
                                    options={quarterOptions}
                                    value={selectedQuarter}
                                    onChange={(option) => setSelectedQuarter(option)}
                                    isClearable
                                    isSearchable
                                    placeholder="All Quarters"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ─── Stat Cards ─── */}
                    <div className="analytics-stats-grid">
                        {/* Total Employees */}
                        <div className="analytics-stat-card">
                            {loading ? (
                                <Skeleton height={64} width="100%" borderRadius={10} />
                            ) : (
                                <>
                                    <div className="analytics-stat-icon indigo">
                                        <MdPeople />
                                    </div>
                                    <div className="analytics-stat-content">
                                        <h3>Total Employees</h3>
                                        <div className="stat-value">{formatNumber(stats.total_employees || 0)}</div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Avg Performance */}
                        <div className="analytics-stat-card">
                            {loading ? (
                                <Skeleton height={64} width="100%" borderRadius={10} />
                            ) : (
                                <>
                                    <div className="analytics-stat-icon emerald">
                                        <MdTrendingUp />
                                    </div>
                                    <div className="analytics-stat-content">
                                        <h3>Avg Performance</h3>
                                        <div className="stat-value">
                                            {typeof stats.average_progress === 'number'
                                                ? `${formatNumber(stats.average_progress)}%`
                                                : '0%'}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Completion Rate */}
                        <div className="analytics-stat-card">
                            {loading ? (
                                <Skeleton height={64} width="100%" borderRadius={10} />
                            ) : (
                                <>
                                    <div className="analytics-stat-icon amber">
                                        <MdBarChart />
                                    </div>
                                    <div className="analytics-stat-content">
                                        <h3>Completion Rate</h3>
                                        <div className="stat-value">
                                            {typeof stats.completion_rate === 'number'
                                                ? `${formatNumber(stats.completion_rate)}%`
                                                : '0%'}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Completed Goals */}
                        <div className="analytics-stat-card">
                            {loading ? (
                                <Skeleton height={64} width="100%" borderRadius={10} />
                            ) : (
                                <>
                                    <div className="analytics-stat-icon rose">
                                        <MdCheckCircle />
                                    </div>
                                    <div className="analytics-stat-content">
                                        <h3>Completed Goals</h3>
                                        <div className="stat-value">
                                            {formatNumber(stats.completed_goals || 0)} / {formatNumber(stats.total_goals || 0)}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ─── Quarterly Trends (Area Chart with gradient) ─── */}
                    <div className="analytics-chart-card" style={{ animationDelay: '280ms' }}>
                        {loading ? (
                            <div style={{ padding: 20 }}>
                                <Skeleton height={300} borderRadius={10} />
                            </div>
                        ) : (
                            <>
                                <div className="analytics-chart-header">
                                    <div>
                                        <h3>Quarterly Goal Completion Rate</h3>
                                        <p className="chart-subtitle">Tracking performance across quarters</p>
                                    </div>
                                </div>
                                <div className="analytics-chart-body">
                                    {quarterlyTrendsData.length === 0 ? (
                                        <div className="analytics-chart-empty">
                                            <MdBarChart />
                                            No quarterly data available for {selectedYear}
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={320}>
                                            <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.35} />
                                                        <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.02} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                                                <XAxis dataKey="quarter" tick={{ fontSize: 12, fill: '#6b7280' }} />
                                                <YAxis
                                                    domain={[0, 100]}
                                                    tickFormatter={(v) => `${formatNumber(Number(v))}%`}
                                                    tick={{ fontSize: 12, fill: '#6b7280' }}
                                                />
                                                <Tooltip content={<CustomTooltip suffix="%" />} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="completion_rate"
                                                    name="Completion Rate"
                                                    stroke="#4F46E5"
                                                    strokeWidth={3}
                                                    fillOpacity={1}
                                                    fill="url(#areaGradient)"
                                                    dot={{ r: 5, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2 }}
                                                    activeDot={{ r: 7, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2 }}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* ─── Two-column: Status Pie + Cost Center Bar ─── */}
                    <div className="analytics-charts-row">

                        {/* Cost Center Performance – Gradient Bar Chart */}
                        <div className="analytics-chart-card" style={{ animationDelay: '420ms' }}>
                            {loading ? (
                                <div style={{ padding: 20 }}>
                                    <Skeleton height={300} borderRadius={10} />
                                </div>
                            ) : (
                                <>
                                    <div className="analytics-chart-header">
                                        <div>
                                            <h3>Cost Center Avg Performance</h3>
                                            <p className="chart-subtitle">Performance comparison across cost centers</p>
                                        </div>
                                    </div>
                                    <div className="analytics-chart-body">
                                        {courseCenterStatsData.length === 0 ? (
                                            <div className="analytics-chart-empty">
                                                <MdBarChart />
                                                No cost center data available
                                            </div>
                                        ) : (
                                            <div style={{ width: '100%', overflowX: 'auto' }}>
                                                <div style={{ minWidth: Math.max(400, courseCenterStatsData.length * 100) }}>
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <BarChart
                                                            data={courseCenterStatsData}
                                                            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                                                        >
                                                            <defs>
                                                                {BAR_GRADIENTS.map(([c1, c2], i) => (
                                                                    <linearGradient key={i} id={BAR_GRADIENT_IDS[i]} x1="0" y1="0" x2="0" y2="1">
                                                                        <stop offset="0%" stopColor={c1} stopOpacity={1} />
                                                                        <stop offset="100%" stopColor={c2} stopOpacity={0.7} />
                                                                    </linearGradient>
                                                                ))}
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                                                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                                                            <YAxis
                                                                domain={[0, 100]}
                                                                tickFormatter={(v) => `${formatNumber(Number(v))}%`}
                                                                tick={{ fontSize: 12, fill: '#6b7280' }}
                                                            />
                                                            <Tooltip content={<CustomTooltip suffix="%" />} />
                                                            <Bar
                                                                dataKey="average_progress"
                                                                name="Avg Performance"
                                                                radius={[6, 6, 0, 0]}
                                                                maxBarSize={48}
                                                            >
                                                                {courseCenterStatsData.map((_, index) => (
                                                                    <Cell
                                                                        key={`bar-${index}`}
                                                                        fill={`url(#${BAR_GRADIENT_IDS[index % BAR_GRADIENT_IDS.length]})`}
                                                                    />
                                                                ))}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ─── Two-column: Department Line + Department Radar ─── */}
                    <div className="analytics-charts-row">
                        {/* Department Performance – Line Chart */}
                        <div className="analytics-chart-card" style={{ animationDelay: '490ms' }}>
                            {loading ? (
                                <div style={{ padding: 20 }}>
                                    <Skeleton height={300} borderRadius={10} />
                                </div>
                            ) : (
                                <>
                                    <div className="analytics-chart-header">
                                        <div>
                                            <h3>Department Avg Performance</h3>
                                            <p className="chart-subtitle">Performance trend by department</p>
                                        </div>
                                    </div>
                                    <div className="analytics-chart-body">
                                        {departmentStatsData.length === 0 ? (
                                            <div className="analytics-chart-empty">
                                                <MdBarChart />
                                                No department data available
                                            </div>
                                        ) : (
                                            <div style={{ width: '100%', overflowX: 'auto' }}>
                                                <div style={{ minWidth: Math.max(400, departmentStatsData.length * 100) }}>
                                                    <ResponsiveContainer width="100%" height={320}>
                                                        <LineChart
                                                            data={departmentStatsData}
                                                            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                                                        >
                                                            <defs>
                                                                <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                                                                    <stop offset="0%" stopColor="#4F46E5" />
                                                                    <stop offset="100%" stopColor="#818CF8" />
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                                                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                                                            <YAxis
                                                                domain={[0, 100]}
                                                                tickFormatter={(v) => `${formatNumber(Number(v))}%`}
                                                                tick={{ fontSize: 12, fill: '#6b7280' }}
                                                            />
                                                            <Tooltip content={<CustomTooltip suffix="%" />} />
                                                            <Legend />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="average_progress"
                                                                name="Avg Performance (%)"
                                                                stroke="url(#lineGradient)"
                                                                strokeWidth={3}
                                                                dot={{ r: 6, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2 }}
                                                                activeDot={{ r: 8, fill: '#6366F1', stroke: '#fff', strokeWidth: 3 }}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                    </div>

                    {/* ─── Footer ─── */}
                    <div className="analytics-footer">
                        2026 © <span style={{ color: '#4F46E5' }}>Casoe Technologies</span>, All Rights Reserved
                    </div>
                </div>
            </div>
        </>
    );
};

export default Analytics;
